import axios from "axios";
import { useState } from "react";
import { useAuth } from "context/useAuth";
import { Send, Info, Lock, Power } from "lucide-react";
import ViewMgtDevicePanel from "../ViewMgtDevicePanel";
import { useAppSettings } from "context/appsettingsContext";
import { errorAsync, infoAsync } from "functions/notification";
import { ViewMgtDeviceCommandButton } from "./ViewMgtDeviceCommandButton";

export default function ViewMgtDeviceCommands({ deviceId, device }: { deviceId?: string | null | undefined, device?: any | undefined | null }) {
  
  const settings = useAppSettings();
  const { authorisedUser } = useAuth();
  const [deliveryMethod, setDeliveryMethod] = useState<"MQTT" | "SMS">("MQTT");

  async function requestStatusAsync() {
    try {
      
      const response = await axios.post(
        settings.statusRequestCommandUrl,
        JSON.stringify({ deviceId: deviceId, fromWeb: true, deliveryMethod: deliveryMethod }),
        { headers: { Authorization: `Bearer ${authorisedUser.accessToken}`, 'Content-Type': 'application/json' } }
      );

      if (response?.status !== 200) {
        throw new Error(response.data);
      }

      await infoAsync(`Sent status request via ${deliveryMethod}`);

    } catch(error: any) {
      await errorAsync(`Failed to send status request via ${deliveryMethod} : ${error.message || error}`, error);
    }
  }

  async function lockAsync(type: string, reqLockState: boolean) {
    try {

      const response = await axios.post(
        settings.handleLockCommandsUrl,
        JSON.stringify({ deviceId: deviceId, reqLockState: `${reqLockState}` === 'true', fromWeb: true, lockType: type.trim().toLowerCase(), lockCode: "-", deliveryMethod: deliveryMethod }),
        { headers: { Authorization: `Bearer ${authorisedUser.accessToken}`, 'Content-Type': 'application/json' } }
      );

      if (response?.status !== 200) {
        throw new Error(response.data);
      }

      await infoAsync(`Sent ${type} ${reqLockState ? 'Lock' : 'Unlock'} via ${deliveryMethod}`);

    } catch(error: any) {
      await errorAsync(`Failed to send ${type} ${reqLockState ? 'Lock' : 'Unlock'} via ${deliveryMethod} : ${error.message || error}`, error);
    }
  }

  async function resetAsync(description: string, type: string, safe: boolean) {
    try {

      const resetType = type
        .trim()
        .toLowerCase()
        .replaceAll('+', '-')
        .replaceAll(' ', '-')
        .replaceAll('--', '-')
        .replaceAll('--', '-')
        .replaceAll('--', '-')
        .replaceAll('--', '-')
        .trim();

      const response = await axios.post(
        settings.handleResetCommandsUrl,
        JSON.stringify({ deviceId: deviceId, resetType: resetType, safe: !!safe, deliveryMethod: deliveryMethod }),
        { headers: { Authorization: `Bearer ${authorisedUser.accessToken}`, 'Content-Type': 'application/json' } }
      );

      if (response?.status !== 200) {
        throw new Error(response.data);
      }

      await infoAsync(`Sent ${description} via ${deliveryMethod}`);

    } catch(error: any) {
      await errorAsync(`Failed to send ${description} via ${deliveryMethod} : ${error.message || error}`, error);
    }
  }

  return (
    <div className="relative w-full">
      <div className="container mx-auto px-4">

        <div className="grid w-full gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-auto">
      
          <ViewMgtDevicePanel title="Delivery Method" icon={<Send size={16} />}>
            <div className="flex w-full p-1">
              <button type="button" className={`w-1/2 px-3 py-1 border text-sm font-medium rounded-l-md transition active:scale-95 ${ deliveryMethod === "MQTT" ? "bg-gray-200" : "bg-white" }`} onClick={() => setDeliveryMethod("MQTT")}>
                MQTT
              </button>
              <button type="button" className={`w-1/2 px-3 py-1 border text-sm font-medium rounded-r-md transition active:scale-95 ${ deliveryMethod === "SMS" ? "bg-gray-200" : "bg-white" }`} onClick={() => setDeliveryMethod("SMS")}>
                SMS
              </button>
            </div>
          </ViewMgtDevicePanel>

          <ViewMgtDevicePanel title="Status" icon={<Info size={16} />}>
            <div className="grid grid-cols-2 gap-x-2 gap-y-2 p-1">
              <ViewMgtDeviceCommandButton onClick={() => requestStatusAsync()}>
                Request Status
              </ViewMgtDeviceCommandButton>
            </div>
          </ViewMgtDevicePanel>

          { device?.isTrackerOnly !== true &&
            <ViewMgtDevicePanel title="Lock" icon={<Lock size={16} />} >
              <div className="italic text-sm p-1">
                Note: Won't work while ignition is on
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-2 p-1">
                <ViewMgtDeviceCommandButton onClick={() => lockAsync("Normal", true)}>
                  Normal Lock
                </ViewMgtDeviceCommandButton>
                <ViewMgtDeviceCommandButton onClick={() => lockAsync("Normal", false)}>
                  Normal Unlock
                </ViewMgtDeviceCommandButton>
                <ViewMgtDeviceCommandButton onClick={() => lockAsync("Admin", true)}>
                  Admin Lock
                </ViewMgtDeviceCommandButton>
                <ViewMgtDeviceCommandButton onClick={() => lockAsync("Admin", false)}>
                  Admin Unlock
                </ViewMgtDeviceCommandButton>
              </div>
              <div className="p-1">
                <ViewMgtDeviceCommandButton onClick={() => lockAsync("Hijack", true)}>
                  Hijack Lock
                </ViewMgtDeviceCommandButton>
              </div>
            </ViewMgtDevicePanel>
          }

          <ViewMgtDevicePanel title="Power" icon={<Power size={16} />} >
            <div className="italic text-sm p-1">
              Note: Long press required
            </div>
            <div className="grid gap-1 p-1">
              <ViewMgtDeviceCommandButton longPress onClick={() => resetAsync("Reset NRF", "NRF", false)}>
                Reset NRF
              </ViewMgtDeviceCommandButton>
              <ViewMgtDeviceCommandButton longPress onClick={() => resetAsync("Reset STM + NRF", "STM + NRF", false)}>
                Reset STM + NRF
              </ViewMgtDeviceCommandButton>
              <ViewMgtDeviceCommandButton longPress onClick={() => resetAsync("Safe reset STM + NRF", "STM + NRF", true)}>
                Safe reset STM + NRF
              </ViewMgtDeviceCommandButton>
            </div>
          </ViewMgtDevicePanel>
        </div>
     </div>
    </div>
  );
}